/* HMX V21 homepage + collection conversion flow */
(() => {
  'use strict';

  const money = (cents) => {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return window.Shopify.formatMoney(cents);
    } catch (_) {}
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const imageUrl = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value.startsWith('//') ? `https:${value}` : value;
    const source = value.src || value.url || '';
    return source.startsWith('//') ? `https:${source}` : source;
  };

  const initFilters = (root) => {
    const cards = [...root.querySelectorAll('[data-hmx-product-card]')];
    const buttons = [...root.querySelectorAll('[data-hmx-filter]')];
    if (!cards.length || !buttons.length) return;

    const count = root.querySelector('[data-hmx-result-count]');
    const empty = root.querySelector('[data-hmx-empty]');
    const grid = root.querySelector('[data-hmx-product-grid]');
    const heading = root.querySelector('[data-hmx-grid-heading]');
    const copy = root.querySelector('[data-hmx-grid-copy]');
    const defaultCopy = copy?.textContent || '';
    const labels = {
      all: ['Shop Every iPhone Case', defaultCopy],
      wallet: ['Phone + Wallet Cases', 'Keep your phone, cards, ID and cash together in one convenient everyday case.'],
      handsfree: ['Hands-Free Crossbody Cases', 'Wear your phone and essentials close while keeping both hands free.'],
      slim: ['Slim & Stylish Cases', 'Choose a polished lower-bulk case for simple everyday protection.'],
      protection: ['Rugged & Protective Cases', 'Explore cases designed for more protective everyday utility.']
    };

    const apply = (requested, shouldScroll = false) => {
      const active = labels[requested] ? requested : 'all';
      let visible = 0;
      cards.forEach((card) => {
        const categories = String(card.dataset.hmxCategories || '').split(/\s+/);
        const show = active === 'all' || categories.includes(active);
        card.hidden = !show;
        if (show) visible += 1;
      });
      buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.hmxFilter === active)));
      if (heading) heading.textContent = labels[active][0];
      if (copy) copy.textContent = labels[active][1];
      if (count) count.textContent = `${visible} ${visible === 1 ? 'case' : 'cases'} shown`;
      if (empty) empty.hidden = visible > 0;

      const url = new URL(window.location.href);
      if (active === 'all') url.searchParams.delete('style');
      else url.searchParams.set('style', active);
      if (!window.Shopify?.designMode) window.history.replaceState({}, '', url.toString());

      if (shouldScroll && grid) {
        const top = grid.getBoundingClientRect().top + window.scrollY - 88;
        window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    };

    buttons.forEach((button) => button.addEventListener('click', () => apply(button.dataset.hmxFilter || 'all', false)));
    apply(new URLSearchParams(window.location.search).get('style') || 'all');
  };

  const initQuickShop = (root) => {
    const modal = root.querySelector('[data-hmx-modal]');
    if (!modal) return;
    const closeButton = root.querySelector('[data-hmx-modal-close]');
    const picture = root.querySelector('[data-hmx-modal-image]');
    const title = root.querySelector('[data-hmx-modal-title]');
    const price = root.querySelector('[data-hmx-modal-price]');
    const optionsHost = root.querySelector('[data-hmx-modal-options]');
    const addButton = root.querySelector('[data-hmx-modal-atc]');
    const status = root.querySelector('[data-hmx-modal-status]');
    const modelHelp = root.querySelector('[data-hmx-model-help]');
    const details = root.querySelector('[data-hmx-modal-details]');
    const cache = new Map();

    let product = null;
    let variant = null;
    let selected = [];
    let opener = null;

    const close = () => {
      if (typeof modal.close === 'function' && modal.open) modal.close();
      else modal.removeAttribute('open');
      document.body.classList.remove('hmx-v21-modal-open');
      opener?.focus?.();
    };

    const priceMarkup = (amount, compareAt = 0) => {
      const compare = Number(compareAt || 0);
      return `<strong>${money(amount)}</strong>${compare > amount ? ` <s>${money(compare)}</s>` : ''}`;
    };

    const updateVariant = () => {
      if (!product || !addButton) return;
      const count = product.options?.length || 0;
      const complete = selected.length === count && selected.every(Boolean);
      variant = complete ? product.variants.find((item) => item.options.every((value, index) => value === selected[index])) : null;
      status?.classList.remove('is-error');
      if (status) status.textContent = '';

      if (!complete) {
        addButton.disabled = true;
        addButton.textContent = 'SELECT YOUR OPTIONS';
        return;
      }
      if (!variant) {
        addButton.disabled = true;
        addButton.textContent = 'COMBINATION UNAVAILABLE';
        return;
      }
      if (price) price.innerHTML = priceMarkup(variant.price, variant.compare_at_price);
      const chosenImage = imageUrl(variant.featured_image);
      if (chosenImage && picture) picture.src = chosenImage;
      if (!variant.available) {
        addButton.disabled = true;
        addButton.textContent = 'SOLD OUT';
        return;
      }
      addButton.disabled = false;
      addButton.textContent = `ADD TO CART · ${money(variant.price)}`;
    };

    const buildOptions = () => {
      if (!optionsHost || !product) return;
      optionsHost.textContent = '';
      const defaultOnly = product.variants?.length === 1 && /default title/i.test(product.variants[0].title || '');
      if (defaultOnly) {
        product.options = [];
        selected = [];
        variant = product.variants[0];
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? `ADD TO CART · ${money(variant.price)}` : 'SOLD OUT';
        return;
      }

      selected = new Array(product.options.length).fill('');
      product.options.forEach((entry, index) => {
        const name = typeof entry === 'string' ? entry : entry?.name || `Option ${index + 1}`;
        const values = [...new Set(product.variants.map((item) => item.options[index]).filter(Boolean))];
        const field = document.createElement('div');
        field.className = 'hmx-v21-modal__field';
        const label = document.createElement('label');
        const id = `hmx-v21-option-${index}-${Date.now()}`;
        label.htmlFor = id;
        label.textContent = name;
        const select = document.createElement('select');
        select.id = id;
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = `Choose ${name}`;
        select.appendChild(placeholder);
        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        });
        if (values.length === 1) {
          select.value = values[0];
          selected[index] = values[0];
        }
        select.addEventListener('change', () => {
          selected[index] = select.value;
          updateVariant();
        });
        field.append(label, select);
        optionsHost.appendChild(field);
        if (/model|device|phone|iphone/i.test(name) && modelHelp) modelHelp.hidden = false;
      });
      updateVariant();
    };

    const fetchProduct = (url) => {
      const key = url.split('?')[0].replace(/\/$/, '');
      if (cache.has(key)) return cache.get(key);
      const request = fetch(`${key}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(async (response) => {
        if (!response.ok) throw new Error('This product could not be loaded.');
        return response.json();
      });
      cache.set(key, request);
      return request;
    };

    const open = async (button) => {
      opener = button;
      product = null;
      variant = null;
      selected = [];
      const url = button.dataset.productUrl;
      const cleanTitle = button.dataset.productTitle || 'Choose your options';
      if (title) title.textContent = cleanTitle;
      if (price) price.textContent = '';
      if (optionsHost) optionsHost.innerHTML = '<p>Loading options…</p>';
      if (picture) { picture.removeAttribute('src'); picture.alt = cleanTitle; }
      if (addButton) { addButton.disabled = true; addButton.textContent = 'LOADING OPTIONS…'; }
      if (status) { status.textContent = ''; status.classList.remove('is-error'); }
      if (modelHelp) modelHelp.hidden = true;
      if (details) details.href = url;
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
      document.body.classList.add('hmx-v21-modal-open');

      try {
        product = await fetchProduct(url);
        if (price) price.innerHTML = priceMarkup(product.price, product.compare_at_price);
        const featured = imageUrl(product.featured_image || product.images?.[0]);
        if (featured && picture) picture.src = featured;
        buildOptions();
      } catch (error) {
        if (optionsHost) optionsHost.textContent = '';
        if (addButton) { addButton.disabled = true; addButton.textContent = 'OPEN PRODUCT PAGE'; }
        if (status) { status.textContent = error.message || 'Could not load this product.'; status.classList.add('is-error'); }
      }
    };

    root.querySelectorAll('[data-hmx-quick-shop]').forEach((button) => {
      button.addEventListener('click', () => open(button));
      button.addEventListener('pointerenter', () => {
        const url = button.dataset.productUrl;
        if (url) fetchProduct(url).catch(() => {});
      }, { passive: true });
    });

    closeButton?.addEventListener('click', close);
    modal.addEventListener('click', (event) => { if (event.target === modal) close(); });
    modal.addEventListener('cancel', (event) => { event.preventDefault(); close(); });

    addButton?.addEventListener('click', async () => {
      if (!variant?.available || addButton.disabled) return;
      addButton.disabled = true;
      addButton.textContent = 'ADDING…';
      if (status) { status.textContent = 'Adding your selected model and color to cart…'; status.classList.remove('is-error'); }
      try {
        const formData = new FormData();
        formData.set('id', String(variant.id));
        formData.set('quantity', '1');
        formData.append('sections', 'cart-drawer,cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: formData
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'This item could not be added.');
        addButton.textContent = 'ADDED TO CART ✓';
        if (status) status.textContent = 'Your selected option was added to cart.';
        const drawer = document.querySelector('cart-drawer');
        const hasSections = typeof result.sections?.['cart-drawer'] === 'string' && typeof result.sections?.['cart-icon-bubble'] === 'string';
        if (drawer && typeof drawer.renderContents === 'function' && hasSections) {
          close();
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'hmx-v21' } }));
        } else {
          window.setTimeout(() => { window.location.href = window.routes?.cart_url || '/cart'; }, 450);
        }
      } catch (error) {
        addButton.disabled = false;
        addButton.textContent = variant ? `ADD TO CART · ${money(variant.price)}` : 'TRY AGAIN';
        if (status) { status.textContent = error.message || 'Could not add this item. Please try again.'; status.classList.add('is-error'); }
      }
    });
  };

  const init = (root) => {
    if (!root || root.dataset.hmxV21Ready === 'true') return;
    root.dataset.hmxV21Ready = 'true';
    initFilters(root);
    initQuickShop(root);
  };

  const boot = (scope = document) => {
    if (scope.matches?.('[data-hmx-shop-root]')) init(scope);
    scope.querySelectorAll?.('[data-hmx-shop-root]').forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
